export class AbortError extends Error {
  constructor(public readonly reason: string = 'cancelled by user', public readonly ignore = true) {
    super(reason);
    this.name = 'AbortError';
  }
}

type Time = { scale: number };
export const time: Time = { scale: 1 };

export function waitForEvent<T extends Event>(
  target: EventTarget,
  event: string,
  options?: AddEventListenerOptions,
  condition?: (event: T) => boolean
): Promise<T> {
  const signal = options?.signal;
  return new Promise((resolve, reject) => {
    const cleanAbortListener = () => signal?.removeEventListener('abort', abortHandler);
    const cleanEventListener = () => target.removeEventListener(event, eventHandler);
    const abortHandler = () => {
      cleanAbortListener();
      cleanEventListener();
      reject(signal?.reason instanceof AbortError ? signal?.reason : new AbortError(signal?.reason));
    };
    const eventHandler: EventListener = (evt: Event) => {
      const e = evt as T;
      if (!condition || condition(e)) {
        cleanAbortListener();
        cleanEventListener();
        resolve(e);
      }
    };

    target.addEventListener(event, eventHandler, { ...options, signal: undefined });
    signal?.addEventListener('abort', abortHandler);
  });
}

export function waitUntil(predicate: () => boolean, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    function checkPredicate() {
      if (signal && signal.aborted) {
        reject(signal?.reason instanceof AbortError ? signal?.reason : new AbortError(signal?.reason));
      } else if (predicate()) {
        resolve();
      } else {
        requestAnimationFrame(checkPredicate);
      }
    }
    checkPredicate();
  });
}

export function waitWhile(predicate: () => boolean, signal?: AbortSignal): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const checkPredicate = () => {
      if (signal && signal.aborted) {
        reject(signal?.reason instanceof AbortError ? signal?.reason : new AbortError(signal?.reason));
      } else if (predicate()) {
        requestAnimationFrame(checkPredicate);
      } else {
        resolve();
      }
    };
    checkPredicate();
  });
}

export function wait(milliseconds: number, signal?: AbortSignal): Promise<void> {
  const scaledMilliseconds = milliseconds / time.scale;
  return new Promise<void>((resolve, reject) => {
    const cleanListener = () => signal?.removeEventListener('abort', abortHandler);
    const timeoutId = setTimeout(() => {
      resolve();
      cleanListener();
    }, scaledMilliseconds);
    const abortHandler = () => {
      cleanListener();
      clearTimeout(timeoutId);
      reject(signal?.reason instanceof AbortError ? signal?.reason : new AbortError(signal?.reason));
    };
    signal?.addEventListener('abort', abortHandler);
  });
}

export function waitForSeconds(seconds: number, signal?: AbortSignal): Promise<void> {
  return wait(seconds * 1000, signal);
}

export function waitForAnimationFrame(signal?: AbortSignal): Promise<number> {
  return new Promise((resolve, reject) => {
    const callback = (timestamp: number) => {
      if (signal && signal.aborted) {
        reject(signal?.reason instanceof AbortError ? signal?.reason : new AbortError(signal?.reason));
        return;
      }
      resolve(timestamp);
    };
    requestAnimationFrame(callback);
  });
}

export function suppressAbortThrow<T>(task: Promise<T>): Promise<{ isCancelled: boolean; value?: T }> {
  return task
    .then((value) => ({ isCancelled: false, value }))
    .catch((error) => {
      if (error instanceof AbortError && error.ignore) {
        return { isCancelled: true, value: undefined };
      } else {
        throw error;
      }
    });
}

export function catchAndIgnoreAbort(e: Error | string | null | undefined) {
  if (e instanceof Error && isIgnorableAbortError(e)) {
    return;
  }
  throw e;
}

export function isAbortError(error: Error): error is AbortError {
  return error instanceof AbortError || (error.name === 'AbortError' && 'source' in error && error.source === 'kahoot');
}

export function isIgnorableAbortError(error: Error): error is AbortError {
  return isAbortError(error) && error.ignore;
}

let globallyIgnoreAbortErrorInitialized = false;
export function globallyIgnoreAbortError() {
  if (globallyIgnoreAbortErrorInitialized) return;
  globallyIgnoreAbortErrorInitialized = true;
  window.addEventListener('unhandledrejection', (event) => {
    if (event.reason instanceof Error && isAbortError(event.reason) && event.reason.ignore) {
      event.preventDefault();
    }
  });
}

export function anyAbortSignal(signals: (AbortSignal | undefined)[]): AbortSignal {
  const controller = new AbortController();
  const isDefined = (signal: AbortSignal | undefined): signal is AbortSignal => signal !== undefined;
  const listeners = signals.filter(isDefined).map((signal) => {
    const listener = () => controller.abort(signal.reason);
    signal.addEventListener('abort', listener);
    return listener;
  });

  controller.signal.addEventListener('abort', () => {
    signals.filter(isDefined).forEach((signal, index) => signal.removeEventListener('abort', listeners[index]));
  });

  return controller.signal;
}
